import { useEffect, useMemo, useState } from "react";
import { runArticleOutlineStage } from "#/services/articleOutlines";
import { useLocation, useNavigate } from "react-router-dom";
import Card from "#/components/shared/card";
import Input from "#/components/shared/input";
import Textarea from "#/components/shared/textarea";
import Typography from "#/components/shared/typography";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import { formatDate } from "#/components/utls/time";
import { useToast } from "#/hooks/ToastContext";
import useArticleOutlinesList from "#/admin/article-outlines/hooks/useArticleOutlinesList";
import useArticleOutlineMutations from "#/admin/article-outlines/hooks/useArticleOutlineMutations";
import useArticleReviewRevision from "#/admin/articles/hooks/useArticleReviewRevision";

type GenerateFormState = {
  term: string;
};

const emptyGenerateForm: GenerateFormState = {
  term: "",
};

export function ArticleOutlinesList() {
  const location = useLocation();
  const navigate = useNavigate();
  const listQuery = useArticleOutlinesList();
  const { generateOutlines, approveOutline } = useArticleOutlineMutations();
  const reviewRevision = useArticleReviewRevision();
  const { showToast } = useToast();
  const [generateState, setGenerateState] = useState<GenerateFormState>(
    emptyGenerateForm
  );
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [pollUntil, setPollUntil] = useState<number | null>(null);
  const [generatedTerms, setGeneratedTerms] = useState<
    Array<{ term: string; startedAt: number; status: "in_progress" | "done" }>
  >([]);
  const [pendingOutlineIds, setPendingOutlineIds] = useState<string[]>([]);
  const [additionalInstructions, setAdditionalInstructions] = useState<
    Record<string, string>
  >({});
  const [stageLimit, setStageLimit] = useState<number>(1);
  const [runningStage, setRunningStage] = useState<
    null | "outline" | "draft" | "review"
  >(null);

  const outlines = useMemo(
    () => (Array.isArray(listQuery.data) ? listQuery.data : []),
    [listQuery.data]
  );

  const getOutlineArticleId = (outline: Record<string, unknown>) => {
    const value = outline.article_id;
    return typeof value === "string" && value.length > 0 ? value : null;
  };

  useEffect(() => {
    const pendingId = (location.state as { pendingOutlineId?: string } | null)
      ?.pendingOutlineId;
    if (pendingId) {
      setPendingOutlineIds((current) =>
        current.includes(pendingId) ? current : [pendingId, ...current]
      );
      navigate(location.pathname + location.search, { replace: true });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  const handleGenerateChange = (value: string) => {
    setGenerateState({ term: value });
    setGenerateError(null);
  };

  const handleGenerate = async () => {
    const trimmedTerm = generateState.term.trim();
    if (!trimmedTerm) {
      setGenerateError("Term is required.");
      return;
    }

    await generateOutlines.mutateAsync(trimmedTerm);
    setGenerateState(emptyGenerateForm);
    showToast("Outline generation started.", "success");
    setPollUntil(Date.now() + 180_000);
    setGeneratedTerms((current) => [
      { term: trimmedTerm, startedAt: Date.now(), status: "in_progress" },
      ...current,
    ]);
  };

  useEffect(() => {
    if (!pollUntil) return;
    if (Date.now() >= pollUntil) {
      setPollUntil(null);
      setGeneratedTerms((current) =>
        current.map((item) =>
          item.status === "in_progress" ? { ...item, status: "done" } : item
        )
      );
      return;
    }

    const intervalId = window.setInterval(() => {
      const now = Date.now();
      if (now >= pollUntil) {
        window.clearInterval(intervalId);
        setPollUntil(null);
        setGeneratedTerms((current) =>
          current.map((item) =>
            item.status === "in_progress" ? { ...item, status: "done" } : item
          )
        );
        return;
      }

      setGeneratedTerms((current) =>
        current.map((item) =>
          item.status === "in_progress" && now - item.startedAt >= 180_000
            ? { ...item, status: "done" }
            : item
        )
      );

      listQuery.refetch();
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [listQuery, pollUntil]);

  const progressItems = useMemo(
    () => generatedTerms.filter((item) => item.status === "in_progress"),
    [generatedTerms]
  );

  const handleRunStage = async (stage: "outline" | "draft" | "review") => {
    try {
      setRunningStage(stage);
      const data = await runArticleOutlineStage({
        stage,
        limit: Math.max(1, stageLimit || 1),
      });
      showToast(
        `${stage} run completed: selected ${data.selected}, skipped ${data.skipped}.`,
        "success"
      );
      listQuery.refetch();
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : `Failed to run ${stage} stage.`,
        "error"
      );
    } finally {
      setRunningStage(null);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <Card className="w-full" variant="beige" elevation={1}>
        <Card.Header variant="beige" divider className="bg-beige-50">
          <div className="flex flex-col gap-1">
            <Typography variant="h4" className="text-brown-900">
              Generate outlines from a term
            </Typography>
            <Typography variant="body2" className="text-brown-600">
              The generator will create and persist multiple outlines.
            </Typography>
          </div>
        </Card.Header>
        <Card.Body className="space-y-4 bg-white">
          <Input
            id="outline-generate-term"
            label="Term"
            placeholder="Lung Cancer"
            value={generateState.term}
            onChange={(event) => handleGenerateChange(event.target.value)}
            fullWidth
          />
          {generateError ? (
            <Typography variant="body2" className="text-terracotta">
              {generateError}
            </Typography>
          ) : null}
        </Card.Body>
        <Card.Footer className="bg-white">
          <ActionButton
            onClick={handleGenerate}
            isLoading={generateOutlines.isPending}
          >
            Generate outlines
          </ActionButton>
        </Card.Footer>
      </Card>

      <Card className="w-full" variant="default" elevation={1}>
        <Card.Header divider>
          <div className="flex flex-col gap-1">
            <Typography variant="h4" className="text-brown-900">
              Manual pipeline stage controls
            </Typography>
            <Typography variant="body2" className="text-brown-600">
              Manually move outlines through outline → draft → review.
            </Typography>
          </div>
        </Card.Header>
        <Card.Body className="bg-white">
          <div className="flex flex-wrap items-end gap-3">
            <Input
              id="outline-stage-limit"
              label="Count"
              type="number"
              min={1}
              value={String(stageLimit)}
              onChange={(event) => {
                const next = Number(event.target.value);
                setStageLimit(Number.isFinite(next) && next > 0 ? next : 1);
              }}
              containerClassName="w-32"
            />
            <ActionButton
              size="sm"
              onClick={() => handleRunStage("outline")}
              isLoading={runningStage === "outline"}
              disabled={Boolean(runningStage)}
            >
              Run outline
            </ActionButton>
            <ActionButton
              size="sm"
              onClick={() => handleRunStage("draft")}
              isLoading={runningStage === "draft"}
              disabled={Boolean(runningStage)}
            >
              Run draft
            </ActionButton>
            <ActionButton
              size="sm"
              onClick={() => handleRunStage("review")}
              isLoading={runningStage === "review"}
              disabled={Boolean(runningStage)}
            >
              Run review
            </ActionButton>
          </div>
        </Card.Body>
      </Card>

      {generatedTerms.length > 0 ? (
        <Card className="w-full" variant="cream" elevation={1}>
          <Card.Header variant="cream" divider className="bg-cream-100">
            <Typography variant="h4" className="text-brown-900">
              Outline generation progress
            </Typography>
          </Card.Header>
          <Card.Body className="bg-white">
            <div className="flex flex-col gap-2">
              {generatedTerms.map((item) => (
                <div
                  key={`${item.term}-${item.startedAt}`}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-beige-200 px-3 py-2"
                >
                  <div className="flex flex-col">
                    <Typography variant="body2" className="text-brown-900">
                      {item.term}
                    </Typography>
                    <Typography variant="caption" className="text-brown-500">
                      {item.status === "in_progress"
                        ? "Generating outlines…"
                        : "Completed"}
                    </Typography>
                  </div>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                      item.status === "in_progress"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-emerald-100 text-emerald-700"
                    }`}
                  >
                    {item.status === "in_progress" ? "In progress" : "Done"}
                  </span>
                </div>
              ))}
            </div>
          </Card.Body>
          {progressItems.length > 0 ? (
            <Card.Footer className="bg-white">
              <Typography variant="body2" className="text-brown-600">
                We’ll refresh outlines every few seconds while generation runs.
              </Typography>
            </Card.Footer>
          ) : null}
        </Card>
      ) : null}

      <Card className="w-full" variant="default" elevation={1}>
        <Card.Header divider>
          <Typography variant="h4" className="text-brown-900">
            Existing outlines
          </Typography>
        </Card.Header>
        <Card.Body className="bg-white">
          {listQuery.isLoading ? (
            <Typography variant="body2" className="text-brown-600">
              Loading outlines…
            </Typography>
          ) : listQuery.isError ? (
            <div className="rounded-lg border border-terracotta/30 bg-cream-50 p-4">
              <Typography variant="body2" className="text-terracotta">
                {listQuery.error instanceof Error
                  ? listQuery.error.message
                  : "Unable to load outlines."}
              </Typography>
              <div className="mt-3">
                <ActionButton size="sm" onClick={() => listQuery.refetch()}>
                  Retry
                </ActionButton>
              </div>
            </div>
          ) : outlines.length === 0 ? (
            <Typography variant="body2" className="text-brown-600">
              No outlines yet.
            </Typography>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-cream-50 text-brown-700">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Diagnosis term</th>
                    <th className="px-4 py-3 font-semibold">Title</th>
                    <th className="px-4 py-3 font-semibold">
                      Additional instructions
                    </th>
                    <th className="px-4 py-3 font-semibold">Updated</th>
                    <th className="px-4 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-beige-100">
                  {outlines.map((outline) => {
                    const title =
                      outline.title ||
                      outline.name ||
                      `Outline ${outline.id.slice(0, 6)}`;
                    const isApproved = Boolean(outline.approved_at);
                    const isPending = pendingOutlineIds.includes(outline.id);
                    const articleId = getOutlineArticleId(outline);
                    const updated =
                      outline.updated_at || outline.created_at || "";
                    return (
                      <tr key={outline.id}>
                        <td className="px-4 py-3 text-brown-700">
                          {outline.diagnosis_term ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-brown-900">
                          <div className="flex flex-wrap items-center gap-2">
                            <span>{title}</span>
                            {isPending ? (
                              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                                Pending
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Textarea
                            id={`outline-instructions-${outline.id}`}
                            aria-label="Additional instructions"
                            placeholder="Optional instructions"
                            value={additionalInstructions[outline.id] ?? ""}
                            onChange={(event) =>
                              setAdditionalInstructions((current) => ({
                                ...current,
                                [outline.id]: event.target.value,
                              }))
                            }
                            rows={2}
                            containerClassName="min-w-[240px]"
                            fullWidth
                          />
                        </td>
                        <td className="px-4 py-3 text-brown-700">
                          {updated ? formatDate(updated) : "Unknown"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <ActionButton
                              size="sm"
                              tone="secondary"
                              onClick={() =>
                                navigate(`/admin/content/outlines/${outline.id}`)
                              }
                            >
                              View
                            </ActionButton>
                            <ActionButton
                              size="sm"
                              tone="primary"
                              disabled={isApproved || approveOutline.isPending}
                              isLoading={approveOutline.isPending}
                              onClick={async () => {
                                await approveOutline.mutateAsync({
                                  id: outline.id,
                                  additionalInstructions:
                                    additionalInstructions[outline.id]?.trim() ||
                                    undefined,
                                });
                                showToast("Outline approved.", "success");
                              }}
                            >
                              Approve
                            </ActionButton>
                            <ActionButton
                              size="sm"
                              tone="secondary"
                              disabled={!articleId || reviewRevision.isPending}
                              isLoading={reviewRevision.isPending}
                              onClick={async () => {
                                if (!articleId) return;
                                await reviewRevision.mutateAsync(articleId);
                              }}
                            >
                              Trigger review
                            </ActionButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default ArticleOutlinesList;
